import { useCallback, useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { ParseError } from 'src/services/downloadFileUrl';
import ListColumn from 'src/componentes/NewList/ListColumn';
import Toolbar from 'src/componentes/NewList/Toolbar';
import Results from 'src/componentes/NewList/Results';
import Button from 'src/componentes/button';
import LoadingContainer from 'src/componentes/loading-container';
import {
  getColumns,
  getFilter,
  getData,
  getPermissions,
  exportList,
} from 'src/componentes/NewList';
import LucideIcon from 'src/componentes/lucide-icon';
import { COLUNAS, ENTIDADES_CUSTOMIZADAS, FILTROS } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import errorHandler from 'src/utils/error-handler';
import SearchSchema from './SearchSchema';
import useAuth from 'src/hooks/useAuth';
import Search from './Search';
import Actions from './Actions';
import EditView from 'src/paginas/gestao-risco/perda-operacional/PerdaOperacionalEditView';
import ContentManager from 'src/componentes/content-manager';
import { getConfiguration } from 'src/services/commonRequests';
import StatusBadge from 'src/componentes/StatusBadge';
import http from 'src/services/httpService';
import { aplicarValorCampoCustomizadoUrl } from 'src/utils/filtros-url';

function PerdaOperacionalListView({
  columnsTag,
  filterTag,
  entityTag,
  endpoint,
  defaultSort,
  defaultSortBy,
  selectedFilter,
  openLastFilter,
  parameters,
  isDisabled = false,
  apenasReunioesUsuarioLogado,
  perdaOperacionalId,
  camposCustomizadosUrl,
  camposMultiSelecaoEmConjuntoUrl,
  ...rest
}) {
  const intl = useIntl();
  const { terms: resources } = useAuth();

  const [updatedItems] = useState([]);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [filter, setFilter] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(defaultSort);
  const [sortFieldBy, setSortFieldBy] = useState(defaultSortBy);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [configuracao, setConfiguracao] = useState({});
  const openedModalByIdRef = useRef(false);
  const filtroAtualRef = useRef(selectedFilter);
  const camposCustomizadosUrlRef = useRef(null);
  const filtroUrlInicialAplicadoRef = useRef(false);

  useEffect(() => {
    getConfiguration('ConfiguracaoPerdaOperacional')(setConfiguracao)();
  }, []);

  useEffect(() => {
    if (searchText && searchText.trim() !== '') {
      setCurrentPage(1);
    }
  }, [searchText]);

  const loadData = useCallback(
    async (selectedFilterUpdated) => {
      try {
        const possuiFiltroAtualizado = selectedFilterUpdated !== undefined;
        setLoading(true);
        const newPermissions = await getPermissions(endpoint, parameters);
        setPermissions(newPermissions);

        let newData = [];

        if (newPermissions.allowView || newPermissions.allowList) {
          await loadColumns();

          let newFilter = possuiFiltroAtualizado
            ? selectedFilterUpdated
            : filtroAtualRef.current;
          if (!possuiFiltroAtualizado && openLastFilter && filterTag)
            newFilter = await getFilter(filterTag, true);

          if (
            !possuiFiltroAtualizado &&
            !filtroUrlInicialAplicadoRef.current &&
            camposCustomizadosUrl &&
            Object.keys(camposCustomizadosUrl).length > 0
          ) {
            if (!camposCustomizadosUrlRef.current) {
              const response = await http.post(
                '/CampoCustomizado/ObterListaCamposCustomizados',
                { tipoItem: entityTag }
              );
              camposCustomizadosUrlRef.current = response.data
                .map((campoCustomizado) => {
                  const chave = Object.keys(camposCustomizadosUrl).find(
                    (nome) =>
                      nome === campoCustomizado.descricaoCampo ||
                      nome === campoCustomizado.campo?.nome
                  );

                  return chave
                    ? aplicarValorCampoCustomizadoUrl(
                        campoCustomizado,
                        camposCustomizadosUrl[chave],
                        camposMultiSelecaoEmConjuntoUrl?.includes(chave)
                      )
                    : null;
                })
                .filter(Boolean);
            }

            newFilter = {
              ...newFilter,
              valor: {
                ...(newFilter?.valor || {}),
                camposCustomizados: camposCustomizadosUrlRef.current,
              },
            };
          }

          filtroUrlInicialAplicadoRef.current = true;
          filtroAtualRef.current = newFilter;
          setFilter(newFilter);
          let valueFilter = {};
          if (newFilter != null) valueFilter = newFilter.valor;

          const model = {
            ...valueFilter,
            searchText,
            apenasReunioesUsuarioLogado,
          };
          newData = await getData(
            endpoint,
            model,
            sortField,
            sortFieldBy,
            pageSize,
            currentPage - 1,
            parameters
          );
          setData(newData.list);
          setCount(newData.count);
        }
        setLoading(false);
        return newData;
      } catch (err) {
        setLoading(false);
        errorHandler(err);
      }
    },
    [
      endpoint,
      parameters,
      searchText,
      pageSize,
      currentPage,
      sortField,
      sortFieldBy,
      selectedFilter,
      openLastFilter,
      filterTag,
      camposCustomizadosUrl,
      camposMultiSelecaoEmConjuntoUrl,
      entityTag,
    ]
  );

  useEffect(() => {
    loadData();
  }, [loadData, pageSize, currentPage, searchText, sortField, sortFieldBy]);

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  });

  const handleColumnsChange = useCallback(async () => {
    await loadColumns();
    await loadData();
  });

  const handleRefresh = useCallback(
    async (filter) => {
      await loadData(filter);
    },
    [loadData]
  );

  const handleChangeCurrentPage = useCallback((newValue) => {
    setCurrentPage(newValue);
  });

  const handleChangePageSize = useCallback((pageSize) => {
    setPageSize(pageSize);
  });

  const handleSortColumn = useCallback((sort) => {
    setSortField(sort);
  });

  const handleSortByColumn = useCallback((sortBy) => {
    setSortFieldBy(sortBy);
  });

  const handleExport = async (fileExtension) => {
    try {
      setLoading(true);

      let valueFilter = {};
      if (filter != null) valueFilter = filter.valor;

      let newSearchModel = {
        searchText,
        ...valueFilter,
      };

      await exportList(
        newSearchModel,
        endpoint,
        sortField,
        currentPage,
        parameters,
        fileExtension
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorHandler(ParseError(err));
    }
  };

  const handleEdit = (item = {}, configuracaoRisco, isNew = false) => {
    ContentManager.addContent(
      <EditView
        item={item}
        openEdit={true}
        handleRefresh={handleRefresh}
        endpoint={endpoint}
        disabled={!isNew && !permissions.allowEdit}
        isNew={isNew}
        configuracao={configuracaoRisco}
      />
    );
  };

  useEffect(() => {
    if (
      perdaOperacionalId &&
      perdaOperacionalId > 0 &&
      !openedModalByIdRef.current
    ) {
      openedModalByIdRef.current = true;
      handleEdit({ id: perdaOperacionalId }, configuracao, false);
    }
  }, [perdaOperacionalId, configuracao]);

  const renderNome = (item, configuracaoRisco) => {
    if (!item.nome) {
      return;
    }

    return (
      <a
        tabIndex="-1"
        onClick={() => handleEdit(item, configuracaoRisco, false)}
        style={{ cursor: 'pointer' }}
      >
        {item.nome}
      </a>
    );
  };

  const renderStatus = (item) => {
    if (!item.status) return;
    return (
      <StatusBadge
        text={intl.formatMessage({ id: item.status?.descricao })}
        textColor={item.status?.corFundo ? item.status?.cor : undefined}
        backgroundColor={item.status?.corFundo || item.status?.cor}
      />
    );
  };

  const renderActions = (item, configuracaoPerda) => (
    <Actions
      onRefresh={handleRefresh}
      item={item}
      showCancelButton={false}
      showDelete={permissions?.allowDelete}
      endpoint={endpoint}
      handleOpenEdit={() => handleEdit(item, configuracaoPerda, false)}
      resources={resources}
    />
  );

  const defaultColumns = [
    <ListColumn
      key="column-idIntegracao-perdaOperacionalListView"
      minWidth={150}
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'idIntegracao' })
      )}
      valueField="idIntegracao"
      default
      sortField="idIntegracao"
    />,
    <ListColumn
      key="column-nome-perdaOperacionalListView"
      minWidth={200}
      headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'nome' }))}
      valueField="nome"
      valueFunction={(item) => renderNome(item, configuracao, false)}
      default
      sortField="nome"
      required
    />,
    <ListColumn
      key="column-area-perdaOperacionalListView"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'area' }, { area: resources.area })
      )}
      minWidth={150}
      valueField="area"
      default
      sortField="area"
    />,
    <ListColumn
      key="column-areaGeradora-perdaOperacionalListView"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'areaGeradora' })
      )}
      minWidth={150}
      valueField="areaGeradora"
      valueFunction={(item) => item.areaGeradora}
      default
      sortField="areaGeradora"
    />,
    <ListColumn
      key="column-responsaveis-perdaOperacionalListView"
      minWidth={150}
      headerText={capitalizeFirstLetter(resources.responsaveis)}
      valueField="responsaveis"
      default
    />,
    <ListColumn
      key="column-dataRegistro-perdaOperacionalListView"
      minWidth={130}
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'labelDataRegistro' })
      )}
      valueField="dataRegistro"
      valueFunction={(item) =>
        item.dataRegistro
          ? new Date(item.dataRegistro).toLocaleDateString()
          : ''
      }
      default
      sortField="dataRegistro"
    />,
    <ListColumn
      key="column-dataPerda-perdaOperacionalListView"
      minWidth={130}
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'labelDataOcorrencia' })
      )}
      valueField="dataPerda"
      valueFunction={(item) =>
        item.dataPerda ? new Date(item.dataPerda).toLocaleDateString() : ''
      }
      default
      sortField="dataPerda"
    />,
    <ListColumn
      key="column-valorPerda-perdaOperacionalListView"
      minWidth={130}
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'labelValorPerda' })
      )}
      valueField="valorPerda"
      valueFunction={(item) =>
        item.valorPerda ? item.valorPerda.toLocaleString() : ''
      }
      default
      sortField="valorPerda"
    />,
    <ListColumn
      key="column-descricao-perdaOperacionalListView"
      minWidth={130}
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'descricao' })
      )}
      valueField="descricao"
      sortField="descricao"
    />,
    <ListColumn
      key="column-status-perdaOperacionalListView"
      minWidth={130}
      headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'status' }))}
      valueField="status"
      valueFunction={renderStatus}
      default
      sortField="status"
    />,
  ];

  async function loadColumns() {
    try {
      const currentColumns = await getColumns(
        defaultColumns,
        entityTag,
        columnsTag
      );
      setColumns(currentColumns.columns);
      setAllColumns(currentColumns.allColumns);
    } catch (err) {
      errorHandler(err);
    }
  }

  const actions = [
    <Button
      key={1}
      className="ml-2"
      onClick={() => handleEdit({}, configuracao, true)}
      type="primary"
    >
      <LucideIcon icon="plus" />
    </Button>,
  ];

  return (
    <LoadingContainer isLoading={isLoading}>
      <Toolbar
        useSavedFilter
        showSearch
        tipoItem={ENTIDADES_CUSTOMIZADAS.PERDA_OPERACIONAL}
        showExportEmail
        showExport
        onExport={handleExport}
        onColumnsChange={handleColumnsChange}
        onSearchChange={handleSearchChange}
        onRefresh={handleRefresh}
        searchText={searchText}
        searchSchema={SearchSchema}
        searchComponent={<Search />}
        enableSearch
        filter={filter}
        filterTag={filterTag}
        columnsTag={columnsTag}
        columns={columns}
        allColumns={allColumns}
        actions={!isDisabled && permissions?.allowCreate && actions}
        filterDrawer
        url={endpoint}
        isLoading={isLoading}
        {...rest}
      />
      <Results
        tipoItem={entityTag}
        resources={resources}
        count={count}
        columns={columns}
        data={data}
        currentPage={currentPage}
        pageSize={pageSize}
        onChangeCurrentPage={handleChangeCurrentPage}
        onChangePageSize={handleChangePageSize}
        onSortColumn={handleSortColumn}
        onSortByColumn={handleSortByColumn}
        sort={sortField}
        sortBy={sortFieldBy}
        updatedItems={updatedItems}
        showMenu={true}
        handleRefreshPage={handleRefresh}
        renderMenu={(item) => renderActions(item, configuracao)}
        {...rest}
      />
    </LoadingContainer>
  );
}

PerdaOperacionalListView.defaultProps = {
  columnsTag: COLUNAS.PERDAS_OPERACIONAIS,
  filterTag: FILTROS.PERDAS_OPERACIONAIS,
  endpoint: '/PerdaOperacional',
  entityTag: null,
  defaultSort: 'Nome',
  defaultSortBy: '',
  selectedFilter: null,
  openLastFilter: true,
  parameters: {},
  showToolbar: true,
};

export default PerdaOperacionalListView;
